import { RiCloseCircleLine } from '@remixicon/react';
import { type FormEvent, type ReactNode, useState } from 'react';

import type { RouteComponentProps, RouteElement } from '@shared/router';
import Link from '@shared/router/link';
import * as history from '@shared/router/location';
import RouteNotFound from '@shared/views/bootstrap/RouteNotFound';

import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';
import { Checkbox } from '@playbook/atoms/inputs/checkbox';
import { TextInput } from '@playbook/atoms/inputs/text-input';

import { ApiError } from '@portal/pkg/api';
import { useKeyActions } from '@portal/pkg/api/keys';
import { Action } from '@portal/pkg/api/types';
import * as routes from '@portal/pkg/routes';
import { Notice } from '@portal/views/_shared/notice';
import { PageHeader } from '@portal/views/_shared/page';
import { KeyPageFrame, useKey } from '@portal/views/keys/KeyPageFrame';
import {
	NotPermitted,
	can,
	formatDate,
	formatDateTime,
	generations,
} from '@portal/views/keys/_shared';

import css from './styles.module.css';

export default function RevokeKeyPage({
	params,
}: RouteComponentProps): RouteElement {
	return (
		<KeyPageFrame>
			<RevokeKey id={params?.['key']?.toString()} />
		</KeyPageFrame>
	);
}

function RevokeKey({ id }: { id: Optional<string> }): ReactNode {
	const key = useKey(id);
	const { revokeKey } = useKeyActions();

	const [confirmation, setConfirmation] = useState('');
	const [includeReplaced, setIncludeReplaced] = useState(false);
	const [error, setError] = useState<Nullable<string>>(null);
	const [revoking, setRevoking] = useState(false);

	if (!key) {
		return <RouteNotFound />;
	}

	// The action is absent on a key that is already revoked, which the server
	// decides — so this covers both "you may not" and "nothing left to do".
	if (!can(key, Action.RevokeKey)) {
		return key.status === 'revoked' ? (
			<Notice variant="information" title="This key is already revoked">
				It stopped working {formatDate(key.revokedAt ?? key.expiresAt)}.
			</Notice>
		) : (
			<NotPermitted what="revoke this key" />
		);
	}

	// Earlier generations inside their grace window are still usable. They hold
	// different key material, so revoking them is not about containing a leak —
	// it is about whether the integration keeps any way in.
	const stillAlive = generations(key)
		.slice(1)
		.filter((generation) => generation.status === 'active');

	async function handleSubmit(event: FormEvent): Promise<void> {
		event.preventDefault();

		if (!key) {
			return;
		}

		setError(null);
		setRevoking(true);

		try {
			await revokeKey(key.id, includeReplaced);

			history.push(routes.Key(key.id));
		} catch (caught) {
			setError(
				caught instanceof ApiError
					? caught.message
					: 'Something went wrong. Try again.'
			);
		} finally {
			setRevoking(false);
		}
	}

	return (
		<div className={css.page}>
			<PageHeader
				title={`Revoke “${key.name}”?`}
				description="Access is revoked immediately."
				breadcrumb={
					<Link href={routes.Key(key.id)}>
						<span className={css.breadcrumb}>← {key.name}</span>
					</Link>
				}
			/>

			<Card>
				<form className={css.form} onSubmit={handleSubmit}>
					{error && (
						<Notice variant="error" title="The key was not revoked">
							{error}
						</Notice>
					)}

					{key.lastUsedAt && (
						<Notice variant="warning" title="Something is using this key">
							Last used {formatDate(key.lastUsedAt)}.
						</Notice>
					)}

					{stillAlive.length > 0 && (
						<Notice
							variant="warning"
							title={`${stillAlive.length} earlier ${stillAlive.length === 1 ? 'generation is' : 'generations are'} still usable`}>
							Rotating leaves the key it replaced working for a grace period.
							Revoking this one does not touch{' '}
							{stillAlive.length === 1 ? 'it' : 'them'}, so the integration
							keeps a way in until{' '}
							{formatDateTime(
								Math.max(
									...stillAlive.map((generation) => generation.expiresAt)
								)
							)}
							.
						</Notice>
					)}

					{stillAlive.length > 0 && (
						<Checkbox
							name="includeReplaced"
							checked={includeReplaced}
							label="Revoke the earlier generations too"
							description="Ends every key in this chain at once. Use this when the private key has leaked."
							onChange={(event) => setIncludeReplaced(event.target.checked)}
						/>
					)}

					<TextInput
						name="confirmation"
						label="Type the key's name to confirm"
						placeholder={key.name}
						value={confirmation}
						onChange={(event) => setConfirmation(event.target.value)}
					/>

					<p className={css.quiet}>
						This cannot be undone. Getting back in means a new key.
					</p>

					<div className={css.actions}>
						<Button
							label="Cancel"
							onClick={() => history.push(routes.Key(key.id))}
						/>
						<Button
							variant="destructive"
							type="submit"
							label="Revoke key"
							leadingIcon={<RiCloseCircleLine />}
							loading={revoking}
							disabled={revoking || confirmation !== key.name}
						/>
					</div>
				</form>
			</Card>
		</div>
	);
}
